import React from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { CartContext } from '../../cart/CartHandler';
import QuoteDescriptionDialog from './QuoteDescriptionDialog';
import InterestSheet, { InterestTrigger, trackInterest } from './InterestSheet';
import { InterestDraft, emptyDraft, loadDraft, saveDraft } from './interestDraft';

// YYYY-MM-DD -> YYYY.MM.DD.
const huDate = (d: string): string => d.replace(/-/g, '.') + '.';

/** Static bottom bar of the ?quote view: name, price, validity, Érdekel / Kérdezek. */
const QuoteBar: React.FC = () => {
    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
    const { quote, quoteCart } = React.useContext(CartContext);
    const [introOpen, setIntroOpen] = React.useState(false);
    const introShown = React.useRef(false);
    const [priceInfoOpen, setPriceInfoOpen] = React.useState(false);
    const [sheetOpen, setSheetOpen] = React.useState(false);
    const [trigger, setTrigger] = React.useState<InterestTrigger>('erdekel');
    const openTracked = React.useRef(false);
    const [draft, setDraft] = React.useState<InterestDraft>(emptyDraft);
    // Facebook visitors only: intro popup with the admin description, once
    const fromFacebook = !!quote && new URLSearchParams(window.location.search).get('utm_source')?.toLowerCase() === 'facebook';
    const quoteId = quote?.id ?? 0;

    React.useEffect(() => {
        if (fromFacebook && !introShown.current) {
            introShown.current = true;
            setIntroOpen(true);
        }
    }, [fromFacebook]);

    React.useEffect(() => {
        if (!quoteId) return;
        setDraft(loadDraft(quoteId));
        if (!openTracked.current) {
            openTracked.current = true;
            trackInterest('ajanlat_megnyitva', quoteId);
        }
    }, [quoteId]);

    const updateDraft = React.useCallback((patch: Partial<InterestDraft>) => {
        setDraft((d) => {
            const next = { ...d, ...patch };
            saveDraft(quoteId, next);
            return next;
        });
    }, [quoteId]);

    const openInterest = () => {
        setTrigger('erdekel');
        setSheetOpen(true);
    };
    // Same form, „Kérdésem van” preselected
    const openQuestion = () => {
        if (!draft.sent) updateDraft({ intent: 'question', step: 2 });
        setTrigger('kerdezek');
        setSheetOpen(true);
    };

    const name = quoteCart ? (quoteCart.reference.trim() || `#${quoteCart.id}`) : '';
    const showPrice = !!quote?.priceValid && quoteCart?.total != null;

    return (
        <AppBar position="relative" sx={{ zIndex: 100, boxShadow: '0px 0px 20px rgba(0,0,0,0.25)', pb: 'env(safe-area-inset-bottom)' }}>
            <Toolbar variant="dense" sx={{ gap: isNarrow ? 1 : 2, minHeight: 56, px: isNarrow ? 1.5 : undefined }}>
                {!isNarrow && (
                    <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                        <Typography noWrap sx={{ color: theme.palette.secondary.main, fontWeight: 'bold' }}>
                            {name}
                        </Typography>
                    </Box>
                )}

                <Stack alignItems={isNarrow ? 'flex-start' : 'center'} sx={{ flex: isNarrow ? '1 1 auto' : '0 0 auto', minWidth: 0 }}>
                    {showPrice && (
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                                {Math.round(quoteCart!.total!).toLocaleString('hu-HU')} Ft
                            </Typography>
                            {!!quote?.priceInfo && (
                                <IconButton aria-label="Mit tartalmaz az ár?" size="small" onClick={() => setPriceInfoOpen(true)} sx={{ p: 0.25, color: '#fff' }}>
                                    <InfoOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                            )}
                        </Stack>
                    )}
                    {quote && !quote.priceValid && (
                        <Typography variant="caption" sx={{ fontSize: '0.9em', bgcolor: '#8b1a1a', color: '#fff', fontWeight: 'bold', borderRadius: 1, px: 1, py: isNarrow ? 0.5 : 1, whiteSpace: 'nowrap' }}>
                            Az árajánlat lejárt
                        </Typography>
                    )}
                    {quote?.priceValid && quote.validUntil && (
                        <Typography variant="caption" noWrap sx={{ maxWidth: '100%' }}>
                            Érvényes: {huDate(quote.validUntil)}-ig
                        </Typography>
                    )}
                </Stack>

                <Stack direction="row" gap={1} justifyContent="flex-end" sx={{ flex: isNarrow ? '0 0 auto' : '1 1 0' }}>
                    <Button
                        variant="outlined"
                        sx={{
                            bgcolor: theme.palette.primary.light, color: '#fff', whiteSpace: 'nowrap',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', color: '#fff' },
                        }}
                        onClick={openQuestion}
                    >
                        Kérdezek
                    </Button>
                    {quote && (
                        <Button
                            variant="contained"
                            sx={{
                                bgcolor: theme.palette.secondary.main, color: theme.palette.primary.main, fontWeight: 'bold', whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#fff', color: theme.palette.primary.main },
                            }}
                            onClick={openInterest}
                        >
                            {draft.sent ? 'Jeleztél ✓' : 'Érdekel'}
                        </Button>
                    )}
                </Stack>
            </Toolbar>
            <QuoteDescriptionDialog
                open={introOpen}
                onClose={() => setIntroOpen(false)}
                description={quote?.description ?? ''}
            />
            <QuoteDescriptionDialog
                open={priceInfoOpen}
                onClose={() => setPriceInfoOpen(false)}
                description={quote?.priceInfo ?? ''}
                title="Mit tartalmaz az ár?"
                buttonLabel="Bezárás"
            />
            {quote && (
                <InterestSheet
                    open={sheetOpen}
                    onClose={() => setSheetOpen(false)}
                    quoteId={quoteId}
                    reference={quoteCart?.reference ?? ''}
                    trigger={trigger}
                    draft={draft}
                    updateDraft={updateDraft}
                />
            )}
        </AppBar>
    );
};

export default QuoteBar;
